import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, CircularProgress, IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import { CheckCircle, LockOutlined, RadioButtonUnchecked, Visibility, VisibilityOff } from '@mui/icons-material';
import { changeDirectInvitePassword } from '../../../actions/passwordSetup';
import { setNotification } from '../../../actions/appNotifications';
import { appNotificationsTypes } from '../../../utils/constants';
import { validatePassword } from '../../../utils/helpers';
import { logAppError } from '../../../utils/logActions';
import logo from '../../../Assets/images/AirswiftPrimaryLogoWhiteTextCopy.png';

const TERMS_URL = 'https://www.airswift.com/terms-conditions';
const PRIVACY_URL = 'https://www.airswift.com/privacy-cookie-policy/';

const RULES = ['charactersqty', 'lowercase', 'uppercase', 'numberorsymbol'];

const inputSx = {
  borderRadius: '8px',
  backgroundColor: '#fff',
  fontSize: '16px',
  '& .MuiOutlinedInput-input': { padding: '14px 16px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D5DDE3' }
};

// Full-page step of the magic link journey; the user only continues to the app once the password is set.
export default function SetPasswordCard({ email, onSuccess }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const submitting = useSelector((s) => !!s.passwordSetup?.changePending);

  const validity = validatePassword(password);
  const isValid = RULES.every((rule) => validity[rule]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return;
    setSubmitError(false);
    try {
      await dispatch(changeDirectInvitePassword(email, password));
      setPassword('');
      dispatch(
        setNotification({
          type: appNotificationsTypes.SUCCESS,
          message: t('dashboard.setpassword.success'),
          duration: 3000
        })
      );
      onSuccess();
    } catch (error) {
      logAppError('PasswordSetup_Submit_Error', { error: error?.message });
      setSubmitError(true);
    }
  };

  return (
    <main className="min-h-screen flex flex-col items-center justify-center bg-[#022D41] px-4 py-10">
      <img src={logo} alt="Airswift" className="h-12 w-auto object-contain mb-8" />
      <form
        onSubmit={handleSubmit}
        aria-labelledby="set-password-title"
        className="flex flex-col w-full max-w-[520px] bg-white rounded-2xl shadow-xl p-6 md:p-8 text-[#022D41]"
        noValidate
      >
        <h2 id="set-password-title" className="text-2xl font-semibold mb-2">
          {t('dashboard.setpassword.title')}
        </h2>
        <p className="text-[#4A5A66] mb-6">{t('dashboard.setpassword.subtitle')}</p>

        <label htmlFor="set-password-email" className="text-sm font-medium mb-2">
          {t('dashboard.settings.email')}
        </label>
        <OutlinedInput
          id="set-password-email"
          value={email || ''}
          readOnly
          sx={{ ...inputSx, backgroundColor: '#F0F4F7', mb: 3 }}
          endAdornment={
            <InputAdornment position="end">
              <LockOutlined sx={{ color: '#6B7C88', fontSize: 20 }} />
            </InputAdornment>
          }
        />

        <label htmlFor="set-password-new" className="text-sm font-medium mb-2">
          {t('dashboard.setpassword.newpassword')}
        </label>
        <OutlinedInput
          id="set-password-new"
          value={password}
          autoFocus
          autoComplete="new-password"
          placeholder={t('dashboard.setpassword.placeholder')}
          onChange={(e) => setPassword(e.target.value)}
          type={showPassword ? 'text' : 'password'}
          sx={inputSx}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label={t(showPassword ? 'dashboard.setpassword.hide' : 'dashboard.setpassword.show')}
                onClick={() => setShowPassword((prev) => !prev)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />

        <ul className="flex flex-col gap-2 my-5">
          {RULES.map((rule) => (
            <li
              key={rule}
              className="flex items-center gap-2 text-sm"
              data-testid={`rule-${rule}`}
              data-met={validity[rule]}
            >
              {validity[rule] ? (
                <CheckCircle sx={{ color: '#22A861', fontSize: 18 }} />
              ) : (
                <RadioButtonUnchecked sx={{ color: '#9AA8B2', fontSize: 18 }} />
              )}
              {t(`common.passwordvalidation.${rule}`)}
            </li>
          ))}
        </ul>

        {submitError && (
          <Alert severity="error" className="mb-4">
            {t('dashboard.setpassword.submiterror')}
          </Alert>
        )}

        <button
          type="submit"
          disabled={!isValid || submitting}
          className="flex items-center justify-center h-12 rounded-lg bg-[#2089BE] text-white font-semibold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('dashboard.setpassword.submit')}
        </button>

        <p className="text-xs text-center text-[#6B7C88] mt-5">
          {t('dashboard.setpassword.agree')}{' '}
          <a href={TERMS_URL} target="_blank" rel="noreferrer" className="underline">
            {t('dashboard.setpassword.terms')}
          </a>{' '}
          {t('dashboard.setpassword.and')}{' '}
          <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="underline">
            {t('dashboard.setpassword.privacy')}
          </a>
          .
        </p>
      </form>
    </main>
  );
}
